import { type ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { CompassMark } from '@bravemove/design-tokens/brand';
import {
  EMPTY_STATE_ARTWORK,
  type EmptyStateIllustration,
} from '@bravemove/design-tokens/brand-images';

export interface EmptyStateProps {
  readonly icon?: ReactNode;
  /** A shared 3D object for screens with a clear theme; replaces the icon. Never for errors. */
  readonly illustration?: EmptyStateIllustration;
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly action?: ReactNode;
  readonly className?: string;
}

export function EmptyState({
  icon,
  illustration,
  title,
  description,
  action,
  className,
}: EmptyStateProps) {
  const artwork = illustration ? EMPTY_STATE_ARTWORK[illustration] : null;
  return (
    <div
      className={cn(
        'flex min-w-0 flex-col items-center justify-center text-center px-5 py-10 sm:px-8 sm:py-12 gap-3',
        className,
      )}
    >
      {artwork ? (
        <img
          src={artwork.src}
          srcSet={artwork.srcSet}
          sizes="7rem"
          width={artwork.width}
          height={artwork.height}
          alt=""
          loading="lazy"
          decoding="async"
          className="mb-1 size-28 object-contain"
        />
      ) : (
        <div
          aria-hidden="true"
          className="relative mb-2 grid size-16 place-items-center rounded-2xl border border-accent/15 bg-accent-soft text-accent-text before:absolute before:inset-1.5 before:rounded-xl before:border before:border-accent/10"
        >
          {icon ?? <CompassMark size={44} color="currentColor" accent="currentColor" />}
        </div>
      )}
      <h3 className="max-w-xl text-h3 font-bold tracking-tight leading-snug text-ink">{title}</h3>
      {description ? (
        <p className="text-body text-ink-2 max-w-md leading-relaxed">{description}</p>
      ) : null}
      {action ? (
        <div className="mt-3 flex max-w-full flex-wrap items-center justify-center gap-3">
          {action}
        </div>
      ) : null}
    </div>
  );
}
